import { useCallback } from 'react';
import { TabPanel } from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import IdleTimeModal from '@/Application/Modals/IdleTimeModal/IdleTimeModal';
import {
  selectBuyNowCartIsSummaryOpen,
  selectBuyNowScheduledDate,
  selectBuyNowTabActive
} from '@/Application/Selectors/buyNow/useBuyNowCart';
import { useBuyNowSkuHistory } from '@/Application/Selectors/buyNow/useBuyNowSkuHistory';
import { store } from '@/Infrastructure/State';
import { selectShippingMethod } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import {
  setScheduledDate,
  setTabActive
} from '@/modules/buy-now/infrastructure/slices/BuyNowSlice';
import {
  type Cart,
  EShippingMethod,
  EShippingType
} from '@/modules/cart/domain/Cart';
import type {
  DeliveryProduct,
  ShippingMethod
} from '@/modules/shippingMethod/domain/ShippingMethod';
import type { DeliveryGroup } from '@/modules/shippingMethod/domain/ShippingMethod';
import type { shippingMethodBodyRequest } from '@/modules/shippingMethod/domain/ShippingMethodRepository';
import type {
  DeliveryGroupDto,
  DeliveryItemDto,
  GeneralCollectOptionDto,
  HttpClientPreferenceParams,
  HttpShippingMethodDraft
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import { useIdleTimer } from '@/Pages/BuyNowPage/hooks/useIdleTimer';
import {
  TAB_PICKUP,
  TAB_SHIPPING
} from '@/Pages/BuyNowPage/PaymentAndShipping/constants';
import type { TabActive } from '@/Pages/BuyNowPage/PaymentAndShipping/interfaces';
import { PickupOptions } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickUpOptions';
import { ProductList } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ProductList/ProductList';
import { ShippingMethodHeader } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingMethods/ShippingMethodHeader';
import { useSendDeliveryOffers } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingMethods/useSendDeliveryOffers';
import { findScheduledOption } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingMethods/utils';
import { ShippingOptions } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingOptions/ShippingOptions';
import useGetShippingGrouping from '@/sections/shippingMethods/hooks/useGetShippingGrouping';
import {
  formatDate,
  getOtherGroups
} from '@/sections/shippingMethods/mappers/shippingMethodMapper';

export const ShippingMethods = () => {
  const dispatch = useAppDispatch();
  const cart = useAppSelector((state) => state.buyNow.cart.data);
  const isSummaryOpen = useAppSelector(selectBuyNowCartIsSummaryOpen);
  const tabActive = useAppSelector(selectBuyNowTabActive);
  const scheduledDate = useAppSelector(selectBuyNowScheduledDate);
  const { shippingMethods, refetchShippingMethods } = useGetShippingGrouping();
  const { getSkuHistory, updateClientPreference, skuHistory } =
    useBuyNowSkuHistory();

  const { isIdle, resetIdle } = useIdleTimer();
  const handleSetTabActive = useCallback(
    (tab: TabActive) => {
      dispatch(setTabActive(tab));
    },
    [dispatch]
  );

  const isShippingTabActive = tabActive === TAB_SHIPPING;
  const isPickupTabActive = tabActive === TAB_PICKUP;

  const generalShippingMethods = shippingMethods?.deliveryGroups?.map(
    (group: DeliveryGroup) => {
      const productList = group?.productList.map(
        (product: DeliveryProduct) => ({
          ...product
        })
      );
      const scheduledOption = group?.shippingOptions?.find(
        (option) => option?.shippingMethod === EShippingMethod.SCHEDULED
      );
      if (scheduledDate && scheduledOption?.selected) {
        const skuHistory = getSkuHistory();

        const date = new Date(
          skuHistory?.clientPreference?.[0]?.deliveryDateFrom ?? ''
        );

        const hasHistory =
          !!skuHistory?.clientPreference?.[0]?.deliveryDateFrom;

        group.shippingOptions = group?.shippingOptions?.map((option) =>
          option?.shippingMethod === EShippingMethod.SCHEDULED
            ? {
                ...option,
                date: hasHistory ? formatDate(date) : scheduledOption?.date
              }
            : option
        );
      }

      return {
        ...group,
        productList,
        shippingOptions: group.shippingOptions
      };
    }
  );

  useSendDeliveryOffers(generalShippingMethods || []);

  const scheduledOptions = generalShippingMethods?.[0]?.scheduledOptions;

  const handleSelectClientSelection = useCallback(
    async (
      deliveryGroupId: string,
      params?: HttpClientPreferenceParams,
      scheduledDateValue?: Date | null
    ) => {
      const group = generalShippingMethods?.find(
        (shippingMethod) => shippingMethod?.id === deliveryGroupId
      );
      if (!group) return;

      const skus = group?.productList?.map((product) => product.productSKU);
      const dataToSend: shippingMethodBodyRequest[] = skus?.map((sku) => {
        const history = getSkuHistory();

        const data: shippingMethodBodyRequest = {
          sku,
          clientPreference: params
            ? [
                {
                  shippingType: params?.shippingType,
                  shippingSubType: params?.shippingSubType,
                  shippingMethod: params?.shippingMethod,
                  deliveryDateFrom: params?.deliveryDateFrom,
                  deliveryDateTo: params?.deliveryDateTo,
                  timeSlot: params?.timeSlot
                },
                ...(history?.clientPreference ?? [])
              ]
            : history?.clientPreference
        };

        // Update SKU history with new client preference
        if (data?.clientPreference?.[0]) {
          updateClientPreference(data?.clientPreference?.[0]);
        }

        return data;
      });

      const updatedResults = await refetchShippingMethods([...dataToSend]);

      if (!updatedResults || !('deliveryGroups' in updatedResults)) return;

      const updatedGroup = updatedResults?.deliveryGroups?.find(
        (dg: DeliveryGroupDto) =>
          dg?.items?.some((item: DeliveryItemDto) =>
            skus?.includes(item?.itemId)
          )
      );

      if (!updatedGroup) return;
      const updatedDeliveryGroupId = updatedGroup?.id;

      let updatedShippingOption;
      if (isIdle) {
        updatedShippingOption = updatedGroup?.shippingOptions?.find(
          (option) => option.selected
        );
      } else {
        updatedShippingOption = updatedGroup?.shippingOptions?.find(
          (option) =>
            option.shippingType === params?.shippingType &&
            option.shippingMethod === params?.shippingMethod &&
            option.shippingSubType === params?.shippingSubType
        );
      }
      if (!updatedShippingOption) {
        console.warn('No matching shipping option found after refetch');
        return;
      }

      const isCollect =
        updatedShippingOption?.shippingType === EShippingType.COLLECT;

      const firstOption = updatedShippingOption
        ?.options?.[0] as GeneralCollectOptionDto;

      let selectedDateOption = firstOption;
      if (params?.deliveryDateFrom) {
        const foundOption = updatedShippingOption?.options?.find(
          (opt) =>
            opt?.deliveryDateFrom?.split('T')?.[0] === params?.deliveryDateFrom
        );
        if (foundOption) {
          selectedDateOption = foundOption as GeneralCollectOptionDto;
        }
      }

      const optionTimeSlot = selectedDateOption?.timeSlots
        ? selectedDateOption?.timeSlots?.[0]
        : undefined;

      const payload: HttpShippingMethodDraft = {
        deliveryGroupId: updatedDeliveryGroupId,
        shippingType: updatedShippingOption?.shippingType,
        shippingSubType: updatedShippingOption?.shippingSubType,
        shippingMethod: updatedShippingOption?.shippingMethod,
        option: {
          shipDate: selectedDateOption?.shipDate ?? '',
          originNodeType:
            updatedShippingOption?.originNodeType ??
            firstOption?.originNodeType ??
            'PROVIDER',
          originNodeCode:
            updatedShippingOption?.originNodeCode ??
            firstOption?.originNodeCode ??
            ''
        }
      };

      if (!isCollect) {
        payload.option.timeSlot = optionTimeSlot;
      } else {
        payload.option.collectNodeCode = firstOption?.collectNodeCode;
      }

      const selectedOptions = getOtherGroups(
        updatedResults,
        shippingMethods as ShippingMethod,
        updatedDeliveryGroupId,
        cart as Cart
      );
      selectedOptions.unshift(payload);

      await store.dispatch(
        selectShippingMethod({
          shippingMethod: selectedOptions,
          cartId: cart?.id ?? ''
        })
      );

      dispatch(setScheduledDate(scheduledDateValue ?? null));
    },
    [
      generalShippingMethods,
      refetchShippingMethods,
      shippingMethods,
      cart,
      getSkuHistory,
      updateClientPreference,
      isIdle,
      dispatch
    ]
  );

  const handleTabChange = useCallback(
    async (tab: TabActive) => {
      handleSetTabActive(tab);

      const group = generalShippingMethods?.[0];
      if (!group) return;

      if (tab === TAB_PICKUP) {
        const firstPickupOption = group.pickupOptions?.[0];
        if (firstPickupOption) {
          await handleSelectClientSelection(group.id, {
            shippingType: firstPickupOption.shippingType,
            shippingMethod: firstPickupOption.shippingMethod,
            shippingSubType: firstPickupOption.shippingSubType
          });
        }
      } else if (tab === TAB_SHIPPING) {
        const firstShippingOption = group.shippingOptions?.[0];
        if (
          firstShippingOption &&
          firstShippingOption.deliveryDateFrom &&
          firstShippingOption.deliveryDateTo
        ) {
          await handleSelectClientSelection(group.id, {
            shippingType: firstShippingOption.shippingType,
            shippingMethod: firstShippingOption.shippingMethod,
            shippingSubType: firstShippingOption.shippingSubType,
            deliveryDateFrom:
              firstShippingOption.deliveryDateFrom.split('T')[0],
            deliveryDateTo: firstShippingOption.deliveryDateTo.split('T')[0]
          });
        }
      }
    },
    [handleSetTabActive, generalShippingMethods, handleSelectClientSelection]
  );

  const handleCalendarDateConfirm = async (scheduledDateValue: Date) => {
    if (scheduledOptions && scheduledDateValue) {
      const scheduledOption = findScheduledOption(
        scheduledDateValue,
        scheduledOptions
      );

      const shippingOption = scheduledOptions[0];
      const group = generalShippingMethods?.[0];

      if (
        group &&
        shippingOption &&
        scheduledOption?.deliveryDateFrom &&
        scheduledOption?.deliveryDateTo
      ) {
        handleSelectClientSelection(
          group.id,
          {
            deliveryDateFrom: scheduledOption.deliveryDateFrom.split('T')[0],
            deliveryDateTo: scheduledOption.deliveryDateTo.split('T')[0],
            shippingMethod: shippingOption.shippingMethod,
            shippingSubType: shippingOption.shippingSubType,
            shippingType: shippingOption.shippingType
          },
          scheduledOption.deliveryDateFrom
            ? new Date(scheduledOption.deliveryDateFrom)
            : null
        );
      }
    }
  };

  const refetchAfterIdle = async () => {
    const group = generalShippingMethods?.[0];
    if (!group) return;
    if (!skuHistory) return;
    resetIdle();
    await handleSelectClientSelection(
      group.id,
      skuHistory.clientPreference[0],
      group.shippingOptions?.[0]?.deliveryDateFrom
        ? new Date(group.shippingOptions?.[0]?.deliveryDateFrom)
        : null
    );
  };

  return (
    <>
      <IdleTimeModal isOpen={isIdle} onClose={() => refetchAfterIdle()} />
      {generalShippingMethods?.map((group: DeliveryGroup) => (
        <section
          className={classNames(
            'co-w-full co-bg-white co-flex co-items-center co-justify-center co-border co-rounded-lg co-border-neutral-300',
            !isSummaryOpen ? 'co-mb-150px tablet_w:co-mb-4' : 'co-mb-4'
          )}
          key={group?.id}
        >
          <div
            className="co-flex co-flex-col co-w-full co-h-full"
            data-testid="co-delivery-card"
          >
            <ShippingMethodHeader
              group={group}
              deliveryGroup={group}
              setTabActive={handleTabChange}
              tabActive={tabActive}
            />

            <div className="co-flex co-flex-col tablet_w:co-grid tablet_w:co-grid-cols-2 co-h-full">
              <ProductList />

              <section className="co-flex co-flex-col tablet_w:co-border-l tablet_w:co-border-l-neutral-300  tablet_w:co-border-t-0">
                <TabPanel name={TAB_SHIPPING} active={isShippingTabActive}>
                  <ShippingOptions
                    {...group}
                    deliveryGroupId={group.id}
                    handleSelectClientSelection={handleSelectClientSelection}
                    handleCalendarDateConfirm={handleCalendarDateConfirm}
                  />
                </TabPanel>

                <TabPanel name={TAB_PICKUP} active={isPickupTabActive}>
                  <PickupOptions
                    {...group}
                    handleSelectClientSelection={handleSelectClientSelection}
                  />
                </TabPanel>
              </section>
            </div>
          </div>
        </section>
      ))}
    </>
  );
};

ShippingMethods.displayName = 'ShippingMethods';

export default ShippingMethods;
